import type { SupabaseClient } from '@supabase/supabase-js';
import type { ArchiveItem, Slide, Mantra, IdentityItem, ArchiveType, CCHItem } from '@/types/types';
import { validateMagicBytes, withTransform } from '../imageUtils';
import { ASSET_BUCKET, storagePathFromValue, removeStorageObject } from './storage';

export async function getArchives(client: SupabaseClient, userId: string): Promise<ArchiveItem[]> {
    const { data } = await client.from('archives').select('*').eq('user_id', userId).order('created_at', { ascending: true });

    return (data || []).map(row => ({
        ...row,
        content: typeof row.content === 'object' && row.content?.markdown ? row.content.markdown : row.content
    }));
}

export async function updateArchive(client: SupabaseClient, userId: string, item: ArchiveItem): Promise<{ id: string }> {
    const contentJson = { markdown: item.content };
    const isNewPlaceholder = item.id.startsWith('new-') || item.id === 'placeholder';

    let existing = null;
    if (!isNewPlaceholder) {
        const { data } = await client.from('archives').select('id').eq('id', item.id).maybeSingle();
        existing = data;
    }

    if (existing) {
        const { error } = await client.from('archives').update({
            type: item.type,
            title: item.title,
            content: contentJson
        }).eq('id', item.id);
        if (error) throw error;
        return { id: item.id };
    }

    const { data, error } = await client.from('archives').insert({
        user_id: userId,
        type: item.type,
        title: item.title,
        content: contentJson,
        ...(!isNewPlaceholder ? { id: item.id } : {})
    }).select('id').single();

    if (!error) return { id: data.id };

    if ((error as { code?: string }).code === '23505') {
        const { data: row } = await client
            .from('archives')
            .select('id')
            .eq('user_id', userId)
            .eq('type', item.type)
            .ilike('title', item.title)
            .maybeSingle();
        if (row) {
            await client.from('archives').update({ content: contentJson }).eq('id', row.id);
            return { id: row.id };
        }
    }
    throw error;
}

export async function deleteArchive(client: SupabaseClient, userId: string, id: string): Promise<void> {
    const { error } = await client.from('archives').delete().eq('id', id).eq('user_id', userId);
    if (error) throw error;
}

export async function seedDefaultArchives(
    client: SupabaseClient,
    userId: string,
    ArchiveTypeEnum: typeof ArchiveType,
): Promise<void> {
    const { data: existing } = await client
        .from('archives')
        .select('type, title')
        .eq('user_id', userId);

    const existingKeys = new Set(
        (existing || []).map(e => `${e.type}::${(e.title || '').toLowerCase()}`)
    );
    const existingTypes = new Set((existing || []).map(e => e.type));

    const defaults = [
        {
            type: ArchiveTypeEnum.VISION_5Y,
            title: 'Vision',
            content: { markdown: '# Vision\n\nWelcome to your Vision page. This is a single-document view focused on your long-term trajectory. Use this space to map out who you want to become and what you want to achieve.' }
        },
        {
            type: ArchiveTypeEnum.LIFE_TODO,
            title: 'Life Checklist',
            content: { markdown: '- [ ] Example task' }
        },
        // D-2: Theory Notes carries exactly one identity, a reading and knowledge
        // library. The old seed framed it as a friction log, a job that already
        // belongs to the cycle problems list, so the section was two things under
        // one name. Safe to change: this function has a single call site, inside
        // completeOnboarding(), so an existing account never re-runs it and the
        // `type::title` dedupe key can never insert a second row.
        {
            type: ArchiveTypeEnum.THEORY_NOTES,
            title: 'How to use Theory Notes',
            content: { markdown: '# How to use Theory Notes\n\nThis is your reading and knowledge library, one note per book, paper, talk or idea worth keeping.\n\nEach note should capture the claim, why you believe it, and what you will do differently because of it.\n\nDaily frictions and blockers do not live here, they live in the cycle problems list and feed the debrief.' }
        },
        {
            type: ArchiveTypeEnum.ROUTINE,
            title: 'Morning Routine',
            content: { markdown: '# Morning Routine:\n- [ ] Get sunlight\n- [ ] Get Water & (Creatine)\n- [ ] 20 Chin-Tucks\n- [ ] Take a look to Vision Board / Mantras\n- [ ] Meditate (5-10mins)' }
        },
        {
            type: ArchiveTypeEnum.ROUTINE,
            title: 'Night Routine',
            content: { markdown: '# Night Routine:\n- [ ] **Start at 21:15**\n    - [ ] Shut everything off → charge devices\n    - [ ] Red lights\n    - [ ] Deep Sleep playlist\n- [ ] Brush teeth + face\n- [ ] Plan tomorrow\n- [ ] Journaling\n\n**9:45PM - 10PM**\n- [ ] Read in bed\n\n**Finish before 22:30**' }
        }
    ];

    const isSingleton = (t: string) => t === ArchiveTypeEnum.VISION_5Y || t === ArchiveTypeEnum.LIFE_TODO;

    for (const item of defaults) {
        const key = `${item.type}::${item.title.toLowerCase()}`;
        if (existingKeys.has(key)) continue;
        if (isSingleton(item.type) && existingTypes.has(item.type)) continue;

        const { error } = await client.from('archives').insert({
            user_id: userId,
            type: item.type,
            title: item.title,
            content: item.content
        });

        if (error && (error as { code?: string }).code !== '23505') {
            throw error;
        }
        existingKeys.add(key);
        existingTypes.add(item.type);
    }
}

export async function getMantras(client: SupabaseClient, userId: string): Promise<string[]> {
    const { data: cycles } = await client.from('cycles').select('mantras').eq('user_id', userId);
    const allMantras = new Set<string>();
    cycles?.forEach((c: { mantras?: string[] }) => c.mantras?.forEach((m: string) => allMantras.add(m)));

    const { data: mantras } = await client
        .from('mantras')
        .select('text')
        .eq('user_id', userId);

    mantras?.forEach(m => allMantras.add(m.text));
    return Array.from(allMantras);
}

export async function getMantraItems(client: SupabaseClient, userId: string): Promise<Mantra[]> {
    const { data } = await client
        .from('mantras')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: true });
    return data || [];
}

export async function addMantra(client: SupabaseClient, userId: string, text: string): Promise<Mantra> {
    const { data, error } = await client.from('mantras').insert({
        user_id: userId,
        text
    }).select().single();
    if (error) throw error;
    return data;
}

export async function deleteMantra(client: SupabaseClient, userId: string, id: string): Promise<void> {
    const { error } = await client.from('mantras').delete().eq('id', id).eq('user_id', userId);
    if (error) throw error;
}

export async function getIdentityItems(client: SupabaseClient, userId: string): Promise<IdentityItem[]> {
    const { data } = await client
        .from('identity_items')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: true });
    return data || [];
}

export async function getAllSkillNames(client: SupabaseClient, userId: string): Promise<string[]> {
    const allNames = new Set<string>();

    const { data: items } = await client
        .from('identity_items')
        .select('name')
        .eq('user_id', userId)
        .eq('type', 'skill');
    items?.forEach(i => allNames.add(i.name));

    const { data: cycles } = await client.from('cycles').select('cch_list').eq('user_id', userId);
    cycles?.forEach((c: { cch_list?: CCHItem[] }) => {
        const skillEntry = c.cch_list?.find((item: CCHItem) => item.category === 'Skills');
        skillEntry?.items?.forEach((name: string) => allNames.add(name));
    });

    return Array.from(allNames);
}

export async function getAllCharacteristicNames(client: SupabaseClient, userId: string): Promise<string[]> {
    const allNames = new Set<string>();

    const { data: items } = await client
        .from('identity_items')
        .select('name')
        .eq('user_id', userId)
        .eq('type', 'characteristic');
    items?.forEach(i => allNames.add(i.name));

    const { data: cycles } = await client.from('cycles').select('cch_list').eq('user_id', userId);
    cycles?.forEach((c: { cch_list?: CCHItem[] }) => {
        const charEntry = c.cch_list?.find((item: CCHItem) => item.category === 'Characteristics');
        charEntry?.items?.forEach((name: string) => allNames.add(name));
    });

    return Array.from(allNames);
}

export async function addIdentityItem(client: SupabaseClient, userId: string, name: string, type: 'skill' | 'characteristic'): Promise<IdentityItem> {
    const { data, error } = await client.from('identity_items').insert({
        user_id: userId,
        name,
        type
    }).select().single();
    if (error) throw error;
    return data;
}

export async function updateIdentityItem(client: SupabaseClient, userId: string, id: string, name: string): Promise<void> {
    const { error } = await client.from('identity_items').update({ name }).eq('id', id).eq('user_id', userId);
    if (error) throw error;
}

export async function deleteIdentityItem(client: SupabaseClient, userId: string, id: string): Promise<void> {
    const { error } = await client.from('identity_items').delete().eq('id', id).eq('user_id', userId);
    if (error) throw error;
}

/** Every user's cinema holds this many slides until they add or remove one. */
export const DEFAULT_SLIDE_COUNT = 6;

export async function getSlides(client: SupabaseClient, userId: string): Promise<Slide[]> {
    const { data } = await client
        .from('slides')
        .select('*')
        .eq('user_id', userId)
        .order('position', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: true });
    if (!data || data.length === 0) return [];

    return data.map((s, i) => {
        const slide: Slide = {
            ...s,
            title: s.title ?? '',
            // `description` is the successor to `caption`; rows written before the
            // untagged migration may still carry only the old column.
            description: s.description ?? '',
            image_url: s.image_url ?? '',
            position: s.position ?? i,
        };
        if (slide.image_url && !slide.image_url.startsWith('http') && slide.image_url.length < 2000) {
            const { data: urlData } = client.storage
                .from(ASSET_BUCKET)
                .getPublicUrl(slide.image_url);
            return { ...slide, image_url: urlData.publicUrl };
        }
        return slide;
    });
}

/** Add an empty slide at the end of the user's cinema. Returns the new row id. */
export async function createSlide(client: SupabaseClient, userId: string): Promise<string> {
    const { data: last } = await client
        .from('slides')
        .select('position')
        .eq('user_id', userId)
        .order('position', { ascending: false, nullsFirst: false })
        .limit(1)
        .maybeSingle();

    const { data, error } = await client
        .from('slides')
        .insert({
            user_id: userId,
            title: null,
            description: null,
            image_url: '',
            position: (last?.position ?? -1) + 1,
        })
        .select('id')
        .single();

    if (error) throw error;
    return data.id;
}

/**
 * Delete a slide and the image it owns. The row goes first: if storage cleanup
 * fails the user still gets the delete they asked for, and the leftover object
 * is collectable with tools/storage-orphans.mjs. Losing the row while keeping a
 * publicly reachable image would be the worse failure.
 */
export async function deleteSlide(client: SupabaseClient, userId: string, id: string): Promise<void> {
    const { data: existing } = await client
        .from('slides')
        .select('image_url, gallery_images')
        .eq('id', id)
        .eq('user_id', userId)
        .maybeSingle();

    const { error } = await client.from('slides').delete().eq('id', id).eq('user_id', userId);
    if (error) throw error;

    const owned = [existing?.image_url, ...(existing?.gallery_images ?? [])]
        .map(storagePathFromValue)
        .filter(Boolean);
    for (const path of owned) await removeStorageObject(client, path);
}

export async function updateSlide(client: SupabaseClient, userId: string, slide: Slide, imageFile?: File): Promise<void> {
    let imageUrl = slide.image_url;

    if (imageFile) {
        const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
        const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB for slides

        const imgExt = (imageFile.name.split('.').pop() || '').toLowerCase();
        if (!ALLOWED_EXTENSIONS.includes(imgExt)) throw new Error(`Invalid file type: .${imgExt}`);
        if (!ALLOWED_MIME_TYPES.includes(imageFile.type)) throw new Error(`Invalid MIME type: ${imageFile.type}`);
        if (imageFile.size > MAX_FILE_SIZE) throw new Error('File too large. Max 5MB.');
        if (!await validateMagicBytes(imageFile)) throw new Error('File content does not match its declared type.');
        // Keyed on the slide id, not the old category name. Category-named paths
        // collided the moment a user could have two slides of the same kind, and
        // they leaked the taxonomy into storage. Old rows keep their historic
        // path until their image is next replaced, at which point the cleanup
        // below removes it.
        const fileExt = imgExt;
        const filePath = `${userId}/slides/${slide.id}.${fileExt}`;

        const { error: uploadError } = await client.storage
            .from(ASSET_BUCKET)
            .upload(filePath, imageFile, { upsert: true });

        if (uploadError) throw uploadError;
        imageUrl = filePath;
    }

    // Addressed by id. The previous lookup keyed on category and used .single(),
    // whose error on multiple matches was never checked, so it silently fell
    // through to the INSERT branch and appended a duplicate row on every save.
    const { data: existing } = await client
        .from('slides')
        .select('id, image_url')
        .eq('id', slide.id)
        .eq('user_id', userId)
        .maybeSingle();

    // Storage lifecycle. Without this the old object is abandoned in a PUBLIC
    // bucket, so an image the user believes they removed stays fetchable at its
    // URL forever. Two ways to supersede an object:
    //   - replace with a different extension (same extension upserts in place,
    //     so there is nothing to clean up)
    //   - remove the image, which arrives as an empty image_url
    // Deleted only AFTER the upload succeeded, so a failed upload cannot
    // destroy the image the user still has.
    const previousPath = storagePathFromValue(existing?.image_url);
    const nextPath = storagePathFromValue(imageUrl);
    if (previousPath && previousPath !== nextPath) {
        await removeStorageObject(client, previousPath);
    }

    // Update only. Slides are created explicitly via createSlide, so a save
    // against a row that no longer exists is a stale client, not a reason to
    // resurrect it as a new slide.
    if (!existing) throw new Error('Slide no longer exists.');

    const { error } = await client.from('slides').update({
        image_url: imageUrl,
        title: slide.title?.trim() ? slide.title.trim() : null,
        description: slide.description?.trim() ? slide.description.trim() : null,
    }).eq('id', existing.id).eq('user_id', userId);
    if (error) throw error;
}

// Re-export withTransform for external consumers that import it from this module
export { withTransform };
